import React from 'react';
import Icon from '../../../components/AppIcon';

const fmtOMR = (v) =>
  v == null || v === 0
    ? '—'
    : `OMR ${Number(v)?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const RankBadge = ({ rank }) => {
  const colors = ['bg-amber-400 text-white', 'bg-slate-400 text-white', 'bg-orange-400 text-white'];
  const icons = ['Trophy', 'Medal', 'Award'];
  if (rank <= 3) {
    return (
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${colors?.[rank - 1]}`}>
        {rank}
      </span>
    );
  }
  return (
    <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground flex-shrink-0">
      {rank}
    </span>
  );
};

const HighlightCard = ({ title, icon, iconColor, iconBg, borderColor, children }) => (
  <div className={`bg-card border ${borderColor} rounded-2xl p-4`}>
    <div className="flex items-center gap-2.5 mb-3.5">
      <div className={`${iconBg} rounded-xl p-2`}>
        <Icon name={icon} size={15} className={iconColor} />
      </div>
      <h4 className="text-sm font-bold text-foreground">{title}</h4>
    </div>
    {children}
  </div>
);

const YearlyHighlights = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2, 3, 4]?.map(i => (
          <div key={i} className="h-44 bg-muted/20 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  const top5Customers = data?.top5Customers || [];
  const top5Deals = data?.top5Deals || [];
  const bestDept = data?.bestDepartment || null;
  const highestProfitProject = data?.highestProfitProject || null;

  const hasData =
    top5Customers?.length > 0 ||
    top5Deals?.length > 0 ||
    bestDept ||
    highestProfitProject;

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-3">
        <div className="w-14 h-14 rounded-full bg-muted/20 flex items-center justify-center">
          <Icon name="BarChart3" size={24} className="opacity-30" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium">No Data Available</p>
          <p className="text-xs text-muted-foreground/60 mt-0.5">No yearly highlights to display</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Top 5 Customers */}
      <HighlightCard
        title="Top 5 Customers by Revenue"
        icon="Users"
        iconColor="text-sky-600"
        iconBg="bg-sky-100 dark:bg-sky-900/40"
        borderColor="border-sky-200 dark:border-sky-800/50"
      >
        {top5Customers?.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No Data Available</p>
        ) : (
          <div className="space-y-2">
            {top5Customers?.map((c, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <RankBadge rank={i + 1} />
                <span className="text-xs text-foreground flex-1 truncate font-medium">{c?.name || 'Unknown'}</span>
                <span className="text-xs font-bold text-sky-600 flex-shrink-0">
                  {fmtOMR(c?.revenue)}
                </span>
              </div>
            ))}
          </div>
        )}
      </HighlightCard>

      {/* Top 5 Deals */}
      <HighlightCard
        title="Top 5 Deals by Value"
        icon="Handshake"
        iconColor="text-amber-600"
        iconBg="bg-amber-100 dark:bg-amber-900/40"
        borderColor="border-amber-200 dark:border-amber-800/50"
      >
        {top5Deals?.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No Data Available</p>
        ) : (
          <div className="space-y-2">
            {top5Deals?.map((d, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <RankBadge rank={i + 1} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground truncate font-medium">{d?.name || 'Unknown'}</p>
                  {d?.client && <p className="text-[10px] text-muted-foreground truncate">{d?.client}</p>}
                </div>
                <span className="text-xs font-bold text-amber-600 flex-shrink-0">
                  {fmtOMR(d?.value)}
                </span>
              </div>
            ))}
          </div>
        )}
      </HighlightCard>

      {/* Best Performing Department */}
      <HighlightCard
        title="Best Performing Department"
        icon="Building2"
        iconColor="text-emerald-600"
        iconBg="bg-emerald-100 dark:bg-emerald-900/40"
        borderColor="border-emerald-200 dark:border-emerald-800/50"
      >
        {!bestDept ? (
          <p className="text-xs text-muted-foreground italic">No Data Available</p>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Trophy" size={16} className="text-amber-500" />
              <p className="text-base font-bold text-emerald-600">{bestDept?.name}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-2 text-center">
                <p className="text-xs font-bold text-emerald-700">{fmtOMR(bestDept?.revenue)}</p>
                <p className="text-[10px] text-muted-foreground">Total Revenue</p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-2 text-center">
                <p className="text-xs font-bold text-emerald-700">{bestDept?.deals || 0}</p>
                <p className="text-[10px] text-muted-foreground">Closed Deals</p>
              </div>
            </div>
          </div>
        )}
      </HighlightCard>

      {/* Highest Profit Project */}
      <HighlightCard
        title="Highest Profit Project"
        icon="TrendingUp"
        iconColor="text-violet-600"
        iconBg="bg-violet-100 dark:bg-violet-900/40"
        borderColor="border-violet-200 dark:border-violet-800/50"
      >
        {!highestProfitProject ? (
          <p className="text-xs text-muted-foreground italic">No Data Available</p>
        ) : (
          <div>
            <p className="text-sm font-bold text-violet-600 leading-tight mb-1">
              {highestProfitProject?.name}
            </p>
            {highestProfitProject?.client && (
              <p className="text-xs text-muted-foreground mb-2">{highestProfitProject?.client}</p>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-violet-50 dark:bg-violet-950/30 rounded-lg p-2 text-center">
                <p className="text-xs font-bold text-violet-700">{fmtOMR(highestProfitProject?.profit)}</p>
                <p className="text-[10px] text-muted-foreground">Profit</p>
              </div>
              <div className="bg-violet-50 dark:bg-violet-950/30 rounded-lg p-2 text-center">
                <p className="text-xs font-bold text-violet-700">{fmtOMR(highestProfitProject?.revenue)}</p>
                <p className="text-[10px] text-muted-foreground">Revenue</p>
              </div>
            </div>
          </div>
        )}
      </HighlightCard>
    </div>
  );
};

export default YearlyHighlights;
